import getCsrf from '../modules/getCsrf';
import getDataLayerObject from '../modules/dataLayerHelper';

/**
 * parentModalName:         string, parent Modal name which will be get a status after submitting form to prevent
 * formId:                  string, name of form
 * formData:                object, collection of submitting fields (example: csrf, action, username and so on)
 * csrfToken:               object, token will be set in submit function to make a valid request
 * hideModalAfterSubmit:    bool, (depends on loading process, do not set this value to "true" if you show the loading spinner inside a button)
 * hideLoadingAfterFinally: bool, (hide loading modal after process is finally)
 * showInlineMessage:       bool, (depend on what you want to show modal or inline message)
 * inlineSuccessMessage:    bool,
 * inlineErrorMessage:      bool,
 * successModal:            string, (name of the success modal)
 * errorModal:              string, (name of the error modal)
 * infoModal:               string, (name of the info modal, eg. successfully confirm DOI)
 * responseCodes:           array, (array of positive status codes where we show the success message)
 */
document.addEventListener('alpine:init', () => {
    window.Alpine.data('form', (config) => ({
        parentModalName: config.parentModalName,
        formId: config.formId ?? '',
        formData: new FormData(),
        csrfToken: '',
        hideModalAfterSubmit: config.hideModalAfterSubmit ?? false,
        hideLoadingAfterFinally: config.hideLoadingAfterFinally ?? true,
        showInlineMessage: config.showInlineMessage ?? false,
        inlineSuccessMessage: false,
        inlineErrorMessage: false,
        successModal: config.successModal ?? '',
        errorModal: config.errorModal ?? '',
        infoModal: config.infoModal ?? '',
        responseCodes: config.responseCodes ?? [200, 201, 202, 503],
        localStorageKey: config.localStorageKey ?? '',
        clearLocalStorageAfterSubmit: config.clearLocalStorageAfterSubmit ?? true,
        async fetchToken() {
            return await getCsrf().then(response => response);
        },
        async submitData(event) {
            // Properties
            const form = event.target;
            let isInvalid = [];
            let formFields = form.parentNode.parentNode.querySelectorAll('.bx-validate');

            // Add external fields outside a <form> container
            const externalFields = document.querySelectorAll(`*[form="${form.id}"]`);
            if(externalFields.length > 0) {
                externalFields.forEach(field => {
                    if(field.parentElement.classList.contains('bx-validate')) {
                        formFields = [...formFields, field.parentNode];
                    }
                });
            }

            // Validate fields before submit
            formFields.forEach(field => {
                const inputElement = field.querySelector('.bx-input');

                if (inputElement.dataset.validate === 'true' && !window.bxValidate.validateField(inputElement)) {
                    isInvalid.push(inputElement);
                }
            });

            if (isInvalid.length) {
                // focus first invalid field and return
                if (isInvalid[0].type === 'hidden') {
                    isInvalid[0].parentNode.querySelector('.bx-dropdown-button').focus();
                } else {
                    isInvalid[0].focus();
                }
                return;
            }

            // show spinner in button
            this.$store.globalStore.loading[this.formId] = true;

            // search for fontawesome check icon in success modal to make it later visible
            let iconSpin = document.querySelector('.wizardSuccessModal .icon-spin');
            let iconCheck = document.querySelector('.wizardSuccessModal .icon-check');

            // Hide modal only if this variable is true (e.g. In Wizard-Step-1 the modal is still present)
            if (this.hideModalAfterSubmit) {
                this.$store.modalStore.currentModalName = this.successModal;
            }

            let formDataEntries = new FormData(document.forms[this.formId]);
            for (let entry of formDataEntries.entries()) {
                if (entry[0] === 'attachment') {
                    // Append each selected file to the FormData object
                    this.formData.append(`${entry[0]}[]`, entry[1]);
                } else {
                    this.formData.append(entry[0], entry[1]);
                }
            }

            // fetch token
            this.csrfToken = await this.fetchToken();

            // set CSRF-Token to submitData object
            this.formData.append(this.csrfToken.name, this.csrfToken.value);
            this.formData.append('referrer_url', window.location.href);

            // Fetch data
            await fetch(String(this.formData.get('action')), {
                    method: 'POST',
                    headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'pragma': 'no-cache',
                    'cache-control': 'no-cache',
                },
                    body: this.formData
                })
                .then(response => response.json())
                .then((data) => {

                    // push "event" into dataLayer object -> GA4 form tracking
                    window.dataLayer.push({'event': 'bx-form-send'});

                    if (this.responseCodes.includes(data.status)) {

                        // Clear localStorage after submit if clearLocalStorageAfterSubmit is true
                        if (this.clearLocalStorageAfterSubmit) {
                            localStorage.removeItem(this.localStorageKey);
                        }

                        // Response with redirect-url
                        if (data.hasOwnProperty('data') && data.data.hasOwnProperty('redirect') && data.data.hasOwnProperty('redirect_url') && data.data.redirect_url !== 'undefined') {
                            window.location = data.data.redirect_url;
                            return;
                        }

                        // if a callback is defined in data, you can call a dynamic function/magic
                        if (data.data != null && data.data.hasOwnProperty('callbacks')) {
                            Object.entries(data.data.callbacks).forEach(entry => {
                                const [callback, args] = entry;
                                this[callback](args);
                            });
                        }

                        // depends on inlineMessage or not we have to make different for set $store.globalStore.currentModal variable
                        if(this.showInlineMessage) {
                            this.$store.modalStore.currentModalName = 'inlineSuccessMessage';
                        } else {
                            this.$store.modalStore.currentModalName = (data.data.hasOwnProperty('infoModal')) ? this.infoModal : this.successModal;
                            if(data.status === 409){
                                // Reset form send button for recommendation invite process
                                this.$store.globalStore.loading[this.formId] = false;
                            }
                        }

                        const message = this.showInlineMessage ?
                            event.target.parentNode.querySelector(`.${this.$store.modalStore.currentModalName} .placeholderEmail`) :
                            document.querySelector(`.${this.$store.modalStore.currentModalName} .placeholderEmail`)

                        // replace email in text
                        if (message) {
                            message.innerHTML = message.innerHTML.replace('{email}', data.data.email);
                        }

                        // CR-2507 - GTM dataLayerPush for enhanced conversions
                        if(this.formData.hasOwnProperty('dataLayerEvent')) {
                            const dataLayerObject = getDataLayerObject(
                                    this.formData['dataLayerEvent'],
                                    this.formData
                            );

                            dataLayer.push(dataLayerObject);
                        }

                        // Inline success Message
                        if (this.showInlineMessage) {
                            this.inlineSuccessMessage = true;
                        }

                        // prevent from opening parentModal
                        this.$store.modalStore[this.parentModalName + 'Status'] = false;

                        // swap icons if found in success modal
                        if (iconSpin != null && iconCheck != null) {
                            iconSpin.classList.add('hidden');
                            iconCheck.classList.remove('hidden');
                        }

                    } else {
                        // show errorModal message

                        // Hide loading (can be an icon or a complete modal)
                        this.$store.globalStore.loading[this.formId] = false;

                        // show error message
                        this.$store.modalStore.currentModalName = this.errorModal;

                        // Inline error Message
                        if (this.showInlineMessage) {
                            this.inlineErrorMessage = true;
                        }
                    }

                })
                .catch((error) => {
                    // Show modal with error message
                    this.$store.modalStore.currentModalName = this.errorModal;

                    // Hide loading (can be an icon or a complete modal)
                    this.$store.globalStore.loading[this.formId] = false;
                })
                .finally(() => {
                    // Stop loading animation after we have a response
                    if (this.hideLoadingAfterFinally) {
                        // Hide loading (can be an icon or a complete modal)
                        this.$store.globalStore.loading[this.formId] = false;
                    }
                })
        },
        showLoadingSpinner() {
            return this.$store.globalStore.loading[this.formId];
        }
    }));
})
